import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Paper,
    Grid,
    FormControlLabel,
    Checkbox,
    CircularProgress,
    useTheme,
    useMediaQuery,
    Radio,
    RadioGroup,
    Divider,
    keyframes,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import moment from 'moment';
import nodeAxiosFirebase from '../../../utils/nodeAxiosFirebase';
import Button from '../../general-components/Button';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TextField from '../../general-components/TextField';

const scaleIn = keyframes`
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const AppointmentScheduler = ({ elementDetails, businessData }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { businessId } = useParams();
    const [selectedServices, setSelectedServices] = useState([]);
    const [selectedDate, setSelectedDate] = useState(moment());
    const [availableSlots, setAvailableSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [loading, setLoading] = useState(false);
    const [services, setServices] = useState([]);
    const [locations, setLocations] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [currentStep, setCurrentStep] = useState('services');
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        notes: '',
    });

    const businessPreference = useSelector((state) => state.core.businessData);

    // Fetch available services on component mount
    useEffect(() => {
        const fetchServices = async () => {
            try {
                const response = await nodeAxiosFirebase({
                    method: 'POST',
                    url: 'public/booking',
                    body: {
                        type: 'services',
                        businessId: businessId
                    }
                });
                setServices(response.services || []);
            } catch (error) {
                toast.error(t('errorLoadingServices'));
                console.error('Error loading services:', error);
            }
        };

        if (businessId) {
            fetchServices();
        }
    }, [businessId]);

    const handleServiceSelection = (service) => {
        setSelectedServices((prev) => {
            if (prev.find((s) => s.id === service.id)) {
                return prev.filter((s) => s.id !== service.id);
            }
            return [...prev, service];
        });
    };

    const handleDateSelection = async (date) => {
        setSelectedDate(date);
        setLoading(true);
        setAvailableSlots([]); // Reset slots when new date is selected
        setSelectedLocation(null); // Reset location when new date is selected

        try {
            const response = await nodeAxiosFirebase({
                method: 'POST',
                url: 'public/booking',
                body: {
                    type: 'availab',
                    date: moment(date).format('YYYY-MM-DD'),
                    elementIds: selectedServices.map(s => 'services/' + s.id),
                    businessId: businessId
                }
            });

            if (response.locations && response.locations.length > 0) {
                setLocations(response.locations);
                // If there's only one location, select it automatically
                if (response.locations.length === 1) {
                    const singleLocation = response.locations[0];
                    setSelectedLocation(singleLocation.locationId);
                    if (singleLocation.availableSlots && singleLocation.availableSlots.length > 0) {
                        setAvailableSlots(singleLocation.availableSlots);
                    } else {
                        setAvailableSlots([]);
                        toast.info(t('noAvailableSlots'));
                    }
                } else {
                    setAvailableSlots([]);
                }
            } else {
                setLocations([]);
                setAvailableSlots([]);
                toast.info(t('noLocationsAvailable'));
            }
        } catch (error) {
            console.error('Error loading slots:', error);
            toast.error(t('errorLoadingSlots'));
            setLocations([]);
            setAvailableSlots([]);
        } finally {
            setLoading(false);
        }
    };

    const handleLocationSelection = (locationId) => {
        setSelectedLocation(locationId);
        const location = locations.find(loc => loc.locationId === locationId);
        if (location && location.availableSlots) {
            setAvailableSlots(location.availableSlots);
        } else {
            setAvailableSlots([]);
            toast.info(t('noAvailableSlots'));
        }
    };

    const handleSlotSelection = (slot) => {
        setSelectedSlot(slot);
        setCurrentStep('details');
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async () => {
        if (!selectedServices.length || !selectedDate || !selectedSlot) {
            toast.error(t('pleaseCompleteAllSteps'));
            return;
        }
        try {
            await nodeAxiosFirebase({
                method: 'POST',
                url: 'public/booking',
                body: {
                    type: 'book',
                    elementIds: selectedServices.map(s => 'services/' + s.id),
                    date: moment(selectedDate).format('YYYY-MM-DD'),
                    selectedSlot: {
                        locationId: selectedLocation,
                        start: selectedSlot?.start,
                        end: selectedSlot?.end,
                    },
                    businessId: businessId,
                    email: formData.email,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    phone: formData.phone,
                    currentPath: elementDetails?.mainElement?.documentPath,
                    blockStructureId: '8KqVx3fwJCo23qSRIb0e',
                },
            });

            setCurrentStep('success');
        } catch (error) {
            toast.error(t('errorBookingAppointment'));
            console.error('Error booking appointment:', error);
        }
    };

    const formatTimeSlot = (slot) => {
        return moment(slot.start).format('HH:mm');
    };

    const renderServicesStep = () => (
        <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
            <Grid container spacing={3}>
                {services.map((service) => (
                    <Grid item xs={12} sm={6} key={service.id}>
                        <Paper
                            elevation={selectedServices.find((s) => s.id === service.id) ? 3 : 0}
                            sx={{
                                p: 3,
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                borderRadius: '10px',
                                border: selectedServices.find((s) => s.id === service.id)
                                    ? `2px solid ${theme.palette.primary.main}`
                                    : '2px solid transparent',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: 3,
                                },
                            }}
                            onClick={() => handleServiceSelection(service)}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={selectedServices.some((s) => s.id === service.id)}
                                        onChange={() => handleServiceSelection(service)}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="h6" sx={{ mb: 1 }}>{service.name}</Typography>
                                        <Typography variant="body1" color="primary" sx={{ mb: 1 }}>
                                            {service?.price === 0 ? t('freePrice') : (service.price + '$')} • {service.duration} {t('minutes')}
                                        </Typography>
                                        {service.description && (
                                            <Typography variant="body2" color="text.secondary">
                                                {service.description}
                                            </Typography>
                                        )}
                                    </Box>
                                }
                            />
                        </Paper>
                    </Grid>
                ))}
            </Grid>
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <Button
                    fullWidth
                    buttonSx={{
                        backgroundColor: businessPreference?.mainColor || '#000000',
                    }}
                    onClick={() => setCurrentStep('date')}
                    disabled={selectedServices.length === 0}
                    label={t('next')}
                />

            </Box>
        </Box>
    );

    const renderDateStep = () => (
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 4, textAlign: 'center' }}>
                {t('selectDateAndTime')}
            </Typography>
            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <Paper elevation={0} sx={{ p: 2, minHeight: 400 }}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <StaticDatePicker
                                value={selectedDate}
                                onChange={handleDateSelection}
                                minDate={moment()}
                                sx={{
                                    width: '100%',
                                    '& .MuiPickersCalendarHeader-root': {
                                        margin: '8px 0',
                                    },
                                    '& .MuiPickersDay-root': {
                                        margin: '2px',
                                        width: '36px',
                                        height: '36px',
                                    },
                                    '& .MuiPickersCalendarHeader-label': {
                                        fontSize: '1.2rem',
                                        fontWeight: 'bold',
                                    },
                                    '& .MuiPickersDay-today': {
                                        border: '2px solid',
                                        borderColor: businessPreference?.mainColor || '#000000',
                                    },
                                    '& .Mui-selected': {
                                        backgroundColor: businessPreference?.mainColor || '#000000',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: businessPreference?.mainColor || '#000000',
                                            opacity: 0.8,
                                        },
                                    },
                                    '& .MuiPickersDay-root:not(.Mui-selected)': {
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        },
                                    },
                                }}
                                disablePast
                                showDaysOutsideCurrentMonth
                            />
                        </LocalizationProvider>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper elevation={0} sx={{ p: 2, height: '100%', minHeight: 400 }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <Box>
                                {locations.length > 1 && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="h6" gutterBottom>
                                            {t('selectLocation')}
                                        </Typography>
                                        <RadioGroup
                                            value={selectedLocation || ''}
                                            onChange={(e) => handleLocationSelection(e.target.value)}
                                        >
                                            {locations.map((location) => (
                                                <FormControlLabel
                                                    key={location.locationId}
                                                    value={location.locationId}
                                                    control={<Radio />}
                                                    label={location.name || `Location ${location.locationId}`}
                                                />
                                            ))}
                                        </RadioGroup>
                                        <Divider sx={{ my: 2 }} />
                                    </Box>
                                )}

                                <Typography variant="h6" gutterBottom>
                                    {t('availableSlots')}
                                </Typography>

                                {locations.length === 0 ? (
                                    <Typography color="text.secondary" align="center">
                                        {t('noLocationsAvailable')}
                                    </Typography>
                                ) : !selectedLocation && locations.length > 1 ? (
                                    <Typography color="text.secondary" align="center">
                                        {t('pleaseSelectLocation')}
                                    </Typography>
                                ) : availableSlots.length === 0 ? (
                                    <Typography color="text.secondary" align="center">
                                        {t('noAvailableSlots')}
                                    </Typography>
                                ) : (
                                    <Grid container spacing={1}>
                                        {availableSlots.map((slot) => (
                                            <Grid item xs={6} sm={4} key={slot.start}>
                                                <Button
                                                    fullWidth
                                                    onClick={() => handleSlotSelection(slot)}
                                                    buttonSx={{
                                                        py: 1.5,
                                                        borderRadius: 2,
                                                        backgroundColor: selectedSlot?.start === slot?.start ? businessPreference?.mainColor || '#000000' : 'transparent',
                                                        color: selectedSlot?.start === slot?.start ? 'white' : businessPreference?.mainColor || '#000000',
                                                        border: '1px solid',
                                                        borderColor: businessPreference?.mainColor || '#000000',
                                                        '&:hover': {
                                                            backgroundColor: selectedSlot?.start === slot?.start ?
                                                                businessPreference?.mainColor || '#000000' :
                                                                'rgba(0, 0, 0, 0.04)',
                                                            opacity: selectedSlot?.start === slot?.start ? 0.8 : 1,
                                                        }
                                                    }}
                                                    label={formatTimeSlot(slot)}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                            </Box>
                        )}
                    </Paper>
                </Grid>
            </Grid>
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                    fullWidth
                    onClick={() => setCurrentStep('services')}
                    variant='text'
                    label={t('back')}
                />
                {selectedSlot && (
                    <Button
                        fullWidth
                        onClick={() => setCurrentStep('details')}
                        buttonSx={{
                            backgroundColor: businessPreference?.mainColor || '#000000',
                            '&:hover': {
                                backgroundColor: businessPreference?.mainColor || '#000000',
                                opacity: 0.8,
                            }
                        }}
                        label={t('next')}
                    />
                )}
            </Box>
        </Box>
    );

    const renderDetailsStep = () => (
        <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 4, textAlign: 'center' }}>
                {t('personalDetails')}
            </Typography>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label={t('firstName')}
                        name="firstName"
                        type="text"
                        keyStructure={'firstName'}
                        value={formData?.firstName}
                        onChange={handleFormChange}
                        required
                        sx={{ mb: 2 }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label={t('lastName')}
                        name="lastName"
                        type="text"
                        keyStructure={'lastName'}
                        value={formData?.lastName}
                        onChange={handleFormChange}
                        required
                        sx={{ mb: 2 }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label={t('email')}
                        name="email"
                        type="email"
                        keyStructure={'email'}
                        value={formData?.email}
                        onChange={handleFormChange}
                        required
                        sx={{ mb: 2 }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label={t('phone')}
                        name="phone"
                        type="phone"
                        keyStructure={'phone'}
                        value={formData?.phone}
                        onChange={handleFormChange}
                        required
                        sx={{ mb: 2 }}
                    />
                </Grid>
            </Grid>
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
                <Button
                    fullWidth
                    variant='text'
                    onClick={() => setCurrentStep('date')}
                    label={t('back')}
                />
                <Button
                    fullWidth
                    onClick={handleSubmit}
                    disabled={
                        !formData.firstName ||
                        !formData.lastName ||
                        !formData.email ||
                        !formData.phone
                    }
                    label={t('complete')}
                />
            </Box>
        </Box>
    );

    const renderSuccessStep = () => (
        <Box sx={{
            maxWidth: 600,
            mx: 'auto',
            p: 4,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3
        }}>
            <CheckCircleOutlineIcon
                sx={{
                    fontSize: 100,
                    color: '#4CAF50',
                    animation: `${scaleIn} 0.5s ease-out`
                }}
            />
            <Typography variant="h4" gutterBottom>
                {t('appointmentBookedSuccessfully')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                {t('appointmentConfirmationDetails')}
            </Typography>

        </Box>
    );

    return (
        <Box sx={{
            width: '100%',
            minHeight: '100%',
            bgcolor: 'background.default',
            py: 4
        }}>
            {currentStep === 'services' && renderServicesStep()}
            {currentStep === 'date' && renderDateStep()}
            {currentStep === 'details' && renderDetailsStep()}
            {currentStep === 'success' && renderSuccessStep()}
        </Box>
    );
};

export default AppointmentScheduler;